import React, { useEffect, useState } from "react";
import api, { fmtINR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { FileText, ClipboardCheck, ShoppingCart, PackageCheck, Wallet, TrendingUp, IndianRupee, CheckCircle2, EyeOff } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid } from "recharts";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";

const KPI = ({ title, value, icon: Icon, accent, hint, testid, to }) => {
  const Wrap = to ? Link : "div";
  return (
    <Wrap to={to} data-testid={testid} className="kpi-card block rounded-md p-5 hover:no-underline">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{title}</div>
          <div className="mt-2 font-display text-3xl font-bold tracking-tight">{value}</div>
          {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
        </div>
        <div className={`w-9 h-9 rounded-md grid place-items-center ${accent}`}>
          <Icon size={16} />
        </div>
      </div>
    </Wrap>
  );
};

const COLORS = ["#F5B301", "#111827", "#3B82F6", "#10B981", "#8B5CF6", "#EC4899", "#F97316", "#06B6D4"];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const { pricingHidden, moduleHidden } = useAuth();
  const chartsHidden = moduleHidden("dashboard_charts");

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-muted-foreground">Loading dashboard…</div>;
  const c = data.counts;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Overview</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Command Center</h1>
          <p className="text-sm text-muted-foreground mt-1">Real-time procurement metrics across all projects.</p>
        </div>
        {!pricingHidden ? (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total Procurement Value</div>
            <div className="font-display text-4xl font-bold text-kabir" data-testid="procurement-value">{fmtINR(data.procurement_value)}</div>
          </div>
        ) : (
          <div className="text-right flex items-center gap-2 text-xs text-muted-foreground bg-secondary/50 px-3 py-2 rounded-md border border-border" data-testid="pricing-hidden-banner">
            <EyeOff size={14} />
            <span>Pricing information is restricted for your role.</span>
          </div>
        )}
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI testid="kpi-pending-indents" title="Pending Indents" value={c.pending_indents} icon={FileText} accent="bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-400" hint={`${c.draft_indents} in draft`} to="/indents" />
        <KPI testid="kpi-pending-prs" title="Purchase Reqs" value={c.total_prs} icon={ClipboardCheck} accent="bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-400" hint="Approved by QS" to="/prs" />
        <KPI testid="kpi-pending-pos" title="Pending POs" value={c.pending_pos} icon={ShoppingCart} accent="bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-400" hint={`${c.approved_pos} approved`} to="/pos" />
        <KPI testid="kpi-pending-grns" title="Open GRNs" value={c.pending_grns} icon={PackageCheck} accent="bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400" hint="Awaiting closure" to="/grns" />
      </div>
      {!pricingHidden && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPI testid="kpi-pending-pvs" title="Pending Payment" value={c.pending_pvs} icon={Wallet} accent="bg-orange-50 text-orange-700 dark:bg-orange-950 dark:text-orange-400" to="/pvs" />
          <KPI testid="kpi-paid-total" title="Paid to Vendors" value={fmtINR(data.paid_total).replace("₹", "₹ ")} icon={IndianRupee} accent="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400" to="/pvs" />
          <KPI testid="kpi-approved-po" title="Approved POs" value={c.approved_pos} icon={CheckCircle2} accent="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400" to="/pos" />
          <KPI testid="kpi-approved-ind" title="Approved Indents" value={c.approved_indents} icon={TrendingUp} accent="bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-400" to="/indents" />
        </div>
      )}
      {pricingHidden && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPI testid="kpi-approved-po" title="Approved POs" value={c.approved_pos} icon={CheckCircle2} accent="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400" to="/pos" />
          <KPI testid="kpi-approved-ind" title="Approved Indents" value={c.approved_indents} icon={TrendingUp} accent="bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-400" to="/indents" />
        </div>
      )}

      {/* Charts */}
      {!chartsHidden && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="p-5 lg:col-span-2 border-border">
              <div className="flex items-baseline justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Monthly Trend</div>
                  <div className="font-display text-lg font-semibold">Procurement Spend</div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.monthly_spend.length ? data.monthly_spend : [{ month: "-", spend: 0 }]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="month" fontSize={11} stroke="#6B7280" />
                  <YAxis fontSize={11} stroke="#6B7280" tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`} />
                  <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                  <Line type="monotone" dataKey="spend" stroke="#F5B301" strokeWidth={3} dot={{ fill: "#F5B301", r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </Card>

            <Card className="p-5 border-border">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Distribution</div>
              <div className="font-display text-lg font-semibold mb-4">Project-wise Spend</div>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={data.project_spend.length ? data.project_spend : [{ name: "No data", spend: 1 }]}
                    dataKey="spend" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={40}
                  >
                    {(data.project_spend.length ? data.project_spend : [{ name: "-" }]).map((p, i) => (
                      <Cell key={p.name || i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1 mt-2">
                {data.project_spend.slice(0, 4).map((p, i) => (
                  <div key={p.name || i} className="flex items-center gap-2 text-xs">
                    <div className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="flex-1 truncate">{p.name}</span>
                    <span className="text-muted-foreground font-mono">{fmtINR(p.spend)}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Top Vendors */}
          <Card className="p-5 border-border">
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Ranking</div>
                <div className="font-display text-lg font-semibold">Top Vendors by Spend</div>
              </div>
            </div>
            {data.top_vendors.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8 text-center">No approved purchase orders yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.top_vendors} layout="vertical" margin={{ left: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
                  <XAxis type="number" fontSize={11} stroke="#6B7280" tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`} />
                  <YAxis type="category" dataKey="name" fontSize={11} stroke="#6B7280" width={130} />
                  <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                  <Bar dataKey="spend" fill="#F5B301" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
